import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAnimatedPresence } from 'hooks/overlay/useAnimatedPresence';
import { useDialogFocus } from 'hooks/overlay/useDialogFocus';
import { useDialogKeyboard } from 'hooks/overlay/useDialogKeyboard';
import { cn } from 'utils/cn';

const PHASE_CLASS = {
  enter: 'ui-overlay--enter',
  idle: 'ui-overlay--idle',
  exit: 'ui-overlay--exit',
} as const;

export type OverlayAlign = 'bottom' | 'center';

export interface OverlayProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  backdropClassName?: string;
  dialogClassName?: string;
  align?: OverlayAlign;
  zIndexClass?: string;
  lockScroll?: boolean;
  usePortal?: boolean;
  closeOnBackdrop?: boolean;
  durationMs?: number;
  role?: string;
  'aria-modal'?: boolean;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  'data-leave-guard-skip'?: boolean;
  backdropAriaLabel?: string;
  blockEscape?: boolean;
  triggerRef?: RefObject<HTMLElement | null>;
}

/**
 * Modal / bottom-sheet shell with backdrop fade + dialog scale/slide enter and exit.
 * Shared primitive for platform and Parashari overlays.
 */
export function Overlay({
  open,
  onClose,
  children,
  className,
  backdropClassName = 'bg-black/65 backdrop-blur-[2px]',
  dialogClassName,
  align = 'bottom',
  zIndexClass = 'z-[10000]',
  lockScroll = true,
  usePortal = true,
  closeOnBackdrop = true,
  durationMs = 460,
  role = 'dialog',
  'aria-modal': ariaModal = true,
  'aria-labelledby': ariaLabelledby,
  'aria-label': ariaLabel,
  'data-leave-guard-skip': dataLeaveGuardSkip,
  backdropAriaLabel = 'Close',
  blockEscape = false,
  triggerRef,
}: OverlayProps) {
  const { mounted, phase } = useAnimatedPresence(open, durationMs);
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogKeyboard(mounted && phase !== 'exit', { onClose, blockEscape });
  useDialogFocus(mounted && phase === 'idle', dialogRef, triggerRef);

  useEffect(() => {
    if (!mounted || !lockScroll) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted, lockScroll]);

  if (!mounted) return null;

  const alignClass =
    align === 'center'
      ? 'items-center justify-center p-4 sm:p-6 md:p-10 lg:p-16'
      : 'items-end justify-center px-4 pb-4 pt-16 sm:items-center sm:p-6 md:p-10';

  const content = (
    <div
      className={cn(
        'ui-overlay fixed inset-0 flex',
        zIndexClass,
        alignClass,
        PHASE_CLASS[phase],
        className
      )}
      role={role}
      aria-modal={ariaModal}
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      data-leave-guard-skip={dataLeaveGuardSkip || undefined}
    >
      <button
        type="button"
        className={cn('ui-overlay__backdrop absolute inset-0', backdropClassName)}
        aria-label={backdropAriaLabel}
        onClick={closeOnBackdrop ? onClose : undefined}
        tabIndex={closeOnBackdrop ? 0 : -1}
      />
      <div
        ref={dialogRef}
        className={cn('ui-overlay__dialog relative z-[1] min-w-0', dialogClassName)}
        onClick={(event) => event.stopPropagation()}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );

  if (usePortal && typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
}